import { FC, useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import CameraView from "../../components/scan/CameraView";
import BusStrip from "../../components/scan/BusStrip";
import { allowsBoarding } from "../../components/scan/ResultCard";
import ResultSlot, { ShownScan } from "../../components/scan/ResultSlot";
import ScanInput from "../../components/scan/ScanInput";
import useQrScanner from "../../hooks/useQrScanner";
import useScanTrips from "../../hooks/useScanTrips";
import { BoardingChannel } from "../../interfaces/endpoints/IBoarding.types";
import { scanTicket, undoBoarding } from "../../services/Boarding.service";
import { deviceRef, failureMessage, feedback, isCoarsePointer, looksScanned, unlockAudio } from "./scanHelpers";
import { scanWithRetry } from "./scanWithRetry";

const ScanTickets: FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const { trips, bus, busDay, choose, follow, release } = useScanTrips();
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [shown, setShown] = useState<ShownScan | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [countsVersion, setCountsVersion] = useState(0);
  const [coarse] = useState(isCoarsePointer);

  // Read inside the camera callback, which is created once.
  const busRef = useRef({ tripId: bus?.tripId ?? null, day: busDay, follow });
  busRef.current = { tripId: bus?.tripId ?? null, day: busDay, follow };

  useEffect(() => {
    window.addEventListener("pointerdown", unlockAudio, { once: true });
    return () => window.removeEventListener("pointerdown", unlockAudio);
  }, []);

  // False when a check is still running, so the camera reads the same code again on the next frame.
  const check = useCallback((code: string, typed: boolean): Promise<boolean> | false => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusy(true);
    const manual = typed && !looksScanned(code);
    const { tripId, day } = busRef.current;
    return scanWithRetry((scanId) =>
      scanTicket({
        code,
        tripId,
        journeyDate: tripId ? day : undefined,
        deviceRef: deviceRef(),
        scanId,
        channel: manual ? BoardingChannel.Manual : BoardingChannel.AdminScanner
      })
    )
      .then((response) => {
        setShown({ kind: "result", value: response.data, shownAt: Date.now() });
        setCountsVersion((v) => v + 1);
        const boardedOn = response.data.ticket?.boardedTripId;
        if (!busRef.current.tripId && boardedOn && allowsBoarding(response.data.outcomeCode)) {
          busRef.current.follow(boardedOn, response.data.attemptId);
        }
        feedback(allowsBoarding(response.data.outcomeCode));
        return true;
      })
      .catch((error: unknown) => {
        // Not a verdict: no invalid buzz, so a valid passenger is never turned away for a lost connection.
        setShown({ kind: "failed", message: failureMessage(error), shownAt: Date.now() });
        return false;
      })
      .finally(() => {
        busyRef.current = false;
        setBusy(false);
      });
  }, []);

  const camera = useQrScanner(videoRef, (text) => check(text, false), true);

  const undo = async (attemptId: number) => {
    setUndoing(true);
    try {
      await undoBoarding(attemptId);
      toast.success("Skanimi u anulua");
      setCountsVersion((v) => v + 1);
      release(attemptId);
      setShown((current) =>
        current?.kind === "result" && current.value.attemptId === attemptId ? null : current
      );
    } catch {
      toast.error("Skanimi nuk u anulua");
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div className="flex w-full max-w-md flex-col gap-3">
      <CameraView videoRef={videoRef} state={camera.state} busy={busy} onRetry={camera.retry}>
        <BusStrip trips={trips} bus={bus} refreshKey={countsVersion} onChange={choose} />
      </CameraView>
      <ResultSlot shown={shown} busy={busy} undoing={undoing} onUndo={undo} />
      <ScanInput
        onSubmit={(code) => check(code, true) || Promise.resolve(false)}
        disabled={busy}
        autoFocus={!coarse}
        refocusKey={shown?.shownAt}
      />
    </div>
  );
};

export default ScanTickets;
